import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getAllBanners } from "../../services/HomePage/BannerApi";

import { FaChevronLeft, FaChevronRight, FaFire, FaStar } from "react-icons/fa";

import useEmblaCarousel from "embla-carousel-react";

export default function SpecializedBanner() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();
  const autoplayRef = useRef(null);

  const BASE_IMAGE_URL = process.env.REACT_APP_BANNER_IMAGE_URL || "https://admintest.medrphalabs.com/BannerImage";

  // ✅ Embla Carousel - No loop
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: false,
      dragFree: false,
      slidesToScroll: 1,
      speed: 10,
      breakpoints: {
        '(min-width: 768px)': { slidesToScroll: 1, align: "start" },
        '(min-width: 1024px)': { slidesToScroll: 1, align: "start" }
      }
    }
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // ✅ Update current index on slide change
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // ✅ Auto-slide function - stops at end
  const startAutoplay = useCallback(() => {
    if (!emblaApi || !isReady || banners.length === 0) return;

    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }

    autoplayRef.current = setInterval(() => {
      if (emblaApi) {
        const current = emblaApi.selectedScrollSnap();
        const total = emblaApi.scrollSnapList().length - 1;
        
        if (current >= total) {
          clearInterval(autoplayRef.current);
          autoplayRef.current = null;
          return;
        }
        
        emblaApi.scrollNext();
      }
    }, 3000);
  }, [emblaApi, isReady, banners]);

  // ✅ Start autoplay when ready
  useEffect(() => {
    if (emblaApi && isReady && banners.length > 0) {
      setTimeout(() => {
        emblaApi.reInit();
        startAutoplay();
      }, 300);
    }
  }, [emblaApi, isReady, banners, startAutoplay]);

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const data = await getAllBanners(2);
        const list = Array.isArray(data) ? data : [];
        const activeBanners = list.filter(
          (item) => item.isActive === true && item.image && item.image.trim() !== ""
        );
        const uniqueBanners = [];
        const seenIds = new Set();
        activeBanners.forEach((item) => {
          if (!seenIds.has(item.id)) {
            seenIds.add(item.id);
            uniqueBanners.push(item);
          }
        });
        setBanners(uniqueBanners);
        setIsReady(true);
      } catch (error) {
        console.error("Fetch Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  const handleBannerClick = (item, e) => {
    e.stopPropagation();
    if (Number(item.productCategoryId) === 0) return;
    navigate(`/brand/${item.productCategoryId}`);
  };

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
        <div className="flex justify-center gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-[220px] h-[260px] bg-gray-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const validBanners = banners.filter((item) => item.image && item.image.trim() !== "");

  if (validBanners.length === 0) {
    return null;
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-white rounded-xl shadow-lg mt-3">
      
      {/* Header */}
      <div className="text-center mb-8">
       
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
          <span className="text-gray-800">Exclusive</span>{" "}
          <span className="text-red-600">Offers & Collections</span>
        </h2>
        <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
          Discover our special deals and limited time offers
        </p>
        <p className="text-xs text-gray-400 mt-3">Showing {validBanners.length} exclusive offers</p>
      </div>

      {/* Carousel with both side arrows always visible */}
      <div className="relative w-full px-8">
        {/* Left Arrow */}
        <button
          onClick={scrollPrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
          focus:outline-none"
          aria-label="Previous slide"
        >
          <FaChevronLeft size={16} />
        </button>

        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex gap-3">
            {validBanners.map((item, i) => {
              const imageName = item.image;
              const imgUrl = `${BASE_IMAGE_URL}/${encodeURIComponent(imageName)}`;
              const hasError = imgErrors[i];

              if (hasError) {
                return (
                  <div key={item.id || i} className="min-w-[220px] max-w-[220px] flex-shrink-0 px-1">
                    <div className="w-[220px] h-[260px] rounded-xl overflow-hidden shadow-md bg-gray-200 flex items-center justify-center">
                      {/* Empty placeholder */}
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id || i}
                  className="min-w-[220px] max-w-[220px] flex-shrink-0 px-1"
                >
                  <div
                    onClick={(e) => handleBannerClick(item, e)}
                    className={`relative w-[220px] h-[260px] 
                    rounded-xl overflow-hidden shadow-md hover:shadow-2xl 
                    transition-all duration-300 bg-white
                    ${Number(item.productCategoryId) === 0 ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <img
                      src={imgUrl}
                      alt="Banner"
                      className="w-full h-full object-cover"
                      onError={() => setImgErrors(prev => ({ ...prev, [i]: true }))}
                      loading="lazy"
                    />

                    {/* Border effect */}
                    <div className="absolute inset-0 rounded-xl border-2 border-transparent hover:border-yellow-400/50 transition-all duration-300" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Arrow */}
        <button
          onClick={scrollNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-20 
          bg-white hover:bg-red-500 text-red-500 hover:text-white 
          shadow-lg p-2.5 rounded-full transition-all duration-300 hover:scale-110
          focus:outline-none"
          aria-label="Next slide"
        >
          <FaChevronRight size={16} />
        </button>
      </div>

      {/* ✅ Slide Counter - Shows current slide / total */}
      {validBanners.length > 0 && (
        <div className="text-center mt-4 text-xs text-gray-400">
          {currentIndex + 1} / {validBanners.length}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}